import { createContext, useState } from 'react';
import { ContactPeople } from '../../../../api/service/accountingFirm/clients/types';
import { INITIAL_CONTACT_PEOPLE, INITIAL_COMPANY_DETAILS, INITIAL_ADDRESS_DETAILS } from '../contants';
import {
  AddressDetails,
  AdminDetails,
  AuthorizedSignatory,
  ClientConsultant,
  ClientFormGroup,
  CollectiveAgreementType,
  CompanyDetails,
  ICompanyClientContext,
  AuthorizedSignatoryOptionItem,
} from '../types';
import { useFormik } from 'formik';
import { formSchema } from './schema';
import { AuthorizedSignatoryGroup } from '../../../superAdmin/CompanyClients/types';

export const EditClientContext = createContext<ICompanyClientContext>({
  companyDetails: INITIAL_COMPANY_DETAILS,
  authorizedSignatoryOptions: [],
  setAuthorizedSignatoryOptions: () => {},
  setCompanyDetails: () => {},

  contractNumber: '',
  setContractNumber: () => {},

  adminDetails: [],
  setAdminDetails: () => {},

  clientConsultants: [],
  setClientConsultants: () => {},

  newClientConsultants: [],
  setNewClientConsultants: () => {},

  signatoryDetails: [],
  setSignatoryDetails: () => {},

  addressDetails: INITIAL_ADDRESS_DETAILS,
  setAddressDetails: () => {},

  salaryContactPeople: INITIAL_CONTACT_PEOPLE,
  setSalaryContactPeople: () => {},
  accountingContactPeople: INITIAL_CONTACT_PEOPLE,
  setAccountingContactPeople: () => {},

  contractEffectiveFrom: '',
  setContractEffectiveFrom: () => {},

  collectiveAgreements: [],
  setCollectiveAgreements: () => {},

  invoiceCostCenter: '',
  setInvoiceCostCenter: () => {},

  invoiceEmailAddress: '',
  setInvoiceEmailAddress: () => {},

  invitationEmail: '',
  setInvitationEmail: () => {},

  vismaAPIKey: '',
  setVismaAPIKey: () => {},

  signatoryAdmins: [],
  setSignatoryAdmins: () => {},

  combinations: [],
  setCombinations: () => {},

  deletedAdmins: [],
  setDeletedAdmins: () => {},

  deletedConsultants: [],
  setDeletedConsultants: () => {},

  isEditMode: true,
  formGroup: {} as ClientFormGroup,

  replaceAdminDetails: () => {},
  replaceAddress: () => {},
  replaceAuthorizedSignatory: () => {},
  replaceCompanyDetails: () => {},
  updateContractNumber: () => {},
  replaceConsultants: () => {},
  replaceConsultant: () => {},
  replaceAccountingContactPeople: () => {},
  replaceSalaryContactPeople: () => {},
  updateAdminDetails: () => {},
  updateAddress: () => {},
  updateAuthorizedSignatory: () => {},
  updateCompanyDetails: () => {},
  updateConsultants: () => {},
  updateAccountingContactPeople: () => {},
  updateSalaryContactPeople: () => {},
  updateContractEffectiveInput: () => {},
  updateInvoicingCostCenter: () => {},
  updateInvoiceEmailAddress: () => {},
  updateInvitationEmail: () => {},
  replaceCollectiveAgreements: () => {},
  updateVismaAPIKey: () => {},
  replaceSignatoryAdmins: () => {},
  replaceCombinations: () => {},
  updateCombinations: () => {},
  appendDeletedAdmin: () => {},
  appendDeletedConsultant: () => {},
  replaceAccessLevel: () => {},
});

type Props = {
  children: JSX.Element;
};

export const EditClientProvider = ({ children }: Props) => {
  const [companyDetails, setCompanyDetails] = useState<CompanyDetails>(INITIAL_COMPANY_DETAILS);
  const [authorizedSignatoryOptions, setAuthorizedSignatoryOptions] = useState<Array<AuthorizedSignatoryOptionItem[]>>([]);
  const [contractNumber, setContractNumber] = useState<string>('');
  const [addressDetails, setAddressDetails] = useState<AddressDetails>(INITIAL_ADDRESS_DETAILS);
  const [adminDetails, setAdminDetails] = useState<Array<AdminDetails>>([]);
  const [clientConsultants, setClientConsultants] = useState<Array<ClientConsultant>>([]);
  const [newClientConsultants, setNewClientConsultants] = useState<Array<ClientConsultant>>([]);
  const [signatoryDetails, setSignatoryDetails] = useState<Array<AuthorizedSignatory>>([]);
  const [salaryContactPeople, setSalaryContactPeople] = useState<ContactPeople>(INITIAL_CONTACT_PEOPLE);
  const [accountingContactPeople, setAccountingContactPeople] = useState<ContactPeople>(INITIAL_CONTACT_PEOPLE);
  const [contractEffectiveFrom, setContractEffectiveFrom] = useState<string>('');
  const [invoiceCostCenter, setInvoiceCostCenter] = useState<string>('');
  const [invoiceEmailAddress, setInvoiceEmailAddress] = useState<string>('');
  const [collectiveAgreements, setCollectiveAgreements] = useState<Array<CollectiveAgreementType>>([]);
  const [invitationEmail, setInvitationEmail] = useState<string>('');
  const [vismaAPIKey, setVismaAPIKey] = useState<string>('');
  const [deletedAdmins, setDeletedAdmins] = useState<Array<string>>([]);
  const [deletedConsultants, setDeletedConsultants] = useState<Array<string>>([]);
  const [signatoryAdmins, setSignatoryAdmins] = useState<Array<AuthorizedSignatory>>([]);
  const [combinations, setCombinations] = useState<Array<AuthorizedSignatoryGroup>>([]);
  const isEditMode = true;

  const CLIENT_INITIAL_VALUES = {
    companyDetails: INITIAL_COMPANY_DETAILS,
    address: INITIAL_ADDRESS_DETAILS,
    contractEffectiveFrom: '',
    invoiceEmailAddress: '',
    invoiceContractCenter: '',
    authorizedSignatory: [],
    admins: [],
    consultants: [],
    salaryContactPeople: { ...INITIAL_CONTACT_PEOPLE },
    accountingContactPeople: { ...INITIAL_CONTACT_PEOPLE },
    invitationEmail: '',
    vismaAPIKey: '',
    allowCorrectoAccess: false,
    allowAdvisorAccess: false,
  };

  const formGroup = useFormik({
    initialValues: CLIENT_INITIAL_VALUES,
    validateOnMount: true,
    validationSchema: formSchema,
    onSubmit: () => {},
  });

  const replaceAdminDetails = (value) => {
    setAdminDetails(value);
    formGroup.setFieldValue('admins', value);
  };

  const updateContractNumber = (inputValue) => {
    setContractNumber(inputValue);
    formGroup.setFieldValue(`contractNumber`, inputValue);
  };

  const replaceAddress = (value) => {
    setAddressDetails(value);
    formGroup.setFieldValue('address', value);
    formGroup.setFieldTouched('address');
  };

  const replaceAuthorizedSignatory = (value) => {
    setSignatoryDetails(value);
    formGroup.setFieldValue('authorizedSignatory', value);
  };

  const replaceCompanyDetails = (value) => {
    setCompanyDetails(value);
    formGroup.setFieldValue('companyDetails', value);
    formGroup.setFieldTouched('companyDetails');
  };

  const replaceConsultants = (value) => {
    setClientConsultants(value);
    formGroup.setFieldValue('consultants', value);
  };
  const replaceConsultant = (value) => {
    formGroup.setFieldValue('consultant', value);
  };
  const replaceAccountingContactPeople = (value) => {
    setAccountingContactPeople(value);
    formGroup.setFieldValue('accountingContactPeople', value);
  };

  const replaceSalaryContactPeople = (value) => {
    setSalaryContactPeople(value);
    formGroup.setFieldValue('salaryContactPeople', value);
  };

  const updateAddress = (inputName, inputValue) => {
    setAddressDetails((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`address.${inputName}`, inputValue);
  };

  const updateAdminDetails = (inputName, inputValue) => {
    setAdminDetails((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`admins.${inputName}`, inputValue);
  };

  const updateAuthorizedSignatory = (inputName, inputValue) => {
    setSignatoryDetails((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`authorizedSignatory.${inputName}`, inputValue);
  };

  const updateCompanyDetails = (inputName, inputValue) => {
    setCompanyDetails((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`companyDetails.${inputName}`, inputValue);
  };

  const updateConsultants = (inputName, inputValue) => {
    setClientConsultants((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`consultants.${inputName}`, inputValue);
  };

  const updateAccountingContactPeople = (inputName, inputValue) => {
    setAccountingContactPeople((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`accountingContactPeople.${inputName}`, inputValue);
  };

  const updateSalaryContactPeople = (inputName, inputValue) => {
    setSalaryContactPeople((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`salaryContactPeople.${inputName}`, inputValue);
  };

  const updateContractEffectiveInput = (inputValue) => {
    setContractEffectiveFrom(inputValue);
    formGroup.setFieldValue(`contractEffectiveFrom`, inputValue);
  };

  const updateInvoicingCostCenter = (inputValue) => {
    setInvoiceCostCenter(inputValue);
    formGroup.setFieldValue(`invoiceContractCenter`, inputValue);
  };

  const updateInvoiceEmailAddress = (inputValue) => {
    setInvoiceEmailAddress(inputValue);
    formGroup.setFieldValue(`invoiceEmailAddress`, inputValue);
  };

  const updateInvitationEmail = (inputValue) => {
    setInvitationEmail(inputValue);
    formGroup.setFieldValue(`invitationEmail`, inputValue);
  };

  const replaceCollectiveAgreements = (inputValue) => {
    setCollectiveAgreements(inputValue);
    formGroup.setFieldValue('collectiveAgreements', inputValue);
  };

  const updateVismaAPIKey = (inputValue) => {
    setVismaAPIKey(inputValue);
    formGroup.setFieldValue(`vismaAPIKey`, inputValue);
  };

  const replaceSignatoryAdmins = (value) => {
    setSignatoryAdmins(value);
    formGroup.setFieldValue('signatoryAdmins', value);
  };

  const replaceCombinations = (value) => {
    setCombinations(value);
    formGroup.setFieldValue('combinations', value);
  };

  const updateCombinations = (inputName, inputValue) => {
    setCombinations((value) => ({
      ...value,
      [inputName]: inputValue,
    }));
    formGroup.setFieldValue(`combinations.${inputName}`, inputValue);
  };

  const appendDeletedAdmin = (value) => {
    if (!isEditMode) {
      return;
    }

    const _deletedAdmins = [...deletedAdmins];
    _deletedAdmins.push(value);

    setDeletedAdmins(_deletedAdmins);
  };

  const appendDeletedConsultant = (value) => {
    if (!isEditMode) {
      return;
    }

    const _deletedConsultants = [...deletedConsultants];
    _deletedConsultants.push(value);

    setDeletedConsultants(_deletedConsultants);
  };

  const replaceAccessLevel = (allowCorrectoAccess, allowAdvisorAccess) => {
    formGroup.setFieldValue(`allowCorrectoAccess`, allowCorrectoAccess);
    formGroup.setFieldValue(`allowAdvisorAccess`, allowAdvisorAccess);
  };

  const context: ICompanyClientContext = {
    companyDetails,
    setCompanyDetails,
    authorizedSignatoryOptions,
    setAuthorizedSignatoryOptions,
    contractNumber,
    setContractNumber,
    clientConsultants,
    setClientConsultants,
    newClientConsultants,
    setNewClientConsultants,
    adminDetails,
    setAdminDetails,
    signatoryDetails,
    setSignatoryDetails,
    addressDetails,
    setAddressDetails,
    salaryContactPeople,
    setSalaryContactPeople,
    accountingContactPeople,
    setAccountingContactPeople,
    contractEffectiveFrom,
    setContractEffectiveFrom,
    isEditMode: true,
    collectiveAgreements,
    setCollectiveAgreements,
    invoiceCostCenter,
    setInvoiceCostCenter,
    invoiceEmailAddress,
    setInvoiceEmailAddress,
    invitationEmail,
    setInvitationEmail,
    vismaAPIKey,
    setVismaAPIKey,
    signatoryAdmins,
    setSignatoryAdmins,
    combinations,
    setCombinations,
    deletedAdmins,
    setDeletedAdmins,
    deletedConsultants,
    setDeletedConsultants,
    formGroup: formGroup as any,
    replaceAdminDetails,
    replaceAddress,
    replaceAuthorizedSignatory,
    replaceCompanyDetails,
    replaceConsultants,
    replaceConsultant,
    replaceAccountingContactPeople,
    replaceSalaryContactPeople,
    updateAdminDetails,
    updateAddress,
    updateAuthorizedSignatory,
    updateCompanyDetails,
    updateContractNumber,
    updateConsultants,
    updateAccountingContactPeople,
    updateSalaryContactPeople,
    updateContractEffectiveInput,
    updateInvoiceEmailAddress,
    updateInvoicingCostCenter,
    updateInvitationEmail,
    replaceCollectiveAgreements,
    updateVismaAPIKey,
    replaceSignatoryAdmins,
    appendDeletedAdmin,
    appendDeletedConsultant,
    replaceCombinations,
    updateCombinations,
    replaceAccessLevel,
  };

  return <EditClientContext.Provider value={context}>{children}</EditClientContext.Provider>;
};
